import { useMutation, type UseMutationResult,useQueryClient } from '@tanstack/react-query';

import { PREORDER_STATUS_DISPLAY_LABEL } from '@ekhodealer/ekho-common';
import { toast } from '@ekhodealer/ekho-common/rev';

import type { PreorderExternalStatus } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { preordersEndpoints } from '@config/server/apiEndpoints';

import { preorders } from './queryKeys';
import { updatePreorderStatusInCache } from './usePreorderCacheUtils';

export interface UpdatePreorderStatusParams {
	preorderId: string;
	status: PreorderExternalStatus;
}

export interface BulkUpdatePreorderStatusParams {
	preorderIds: string[];
	status: PreorderExternalStatus;
}

const updatePreorderStatus = async (
	servicesProviderId: string,
	preorderId: string,
	status: string,
): Promise<void> => {
	await apiClient.patch(preordersEndpoints.updateStatus(servicesProviderId, preorderId), {
		newStatus: status,
	});
};

export const useUpdatePreorderStatus = (): UseMutationResult<void, Error, UpdatePreorderStatusParams> => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ preorderId, status }: UpdatePreorderStatusParams): Promise<void> => {
			if (!servicesProviderId) {
				throw new Error('Cannot update preorder status without services provider ID');
			}
			return updatePreorderStatus(servicesProviderId, preorderId, status);
		},
		onSuccess: (_data, { preorderId, status }) => {
			const displayStatus = PREORDER_STATUS_DISPLAY_LABEL[status] ?? status;
			updatePreorderStatusInCache({
				queryClient,
				preorderId,
				newDisplayStatus: displayStatus,
			});
			toast({ message: 'Preorder status updated', variant: 'success' });
			// Delay invalidation — Algolia indexes asynchronously, so an immediate refetch
			// would return stale data and overwrite our cache update
			setTimeout(() => queryClient.invalidateQueries({ queryKey: preorders._def }), 3000);
		},
		onError: (error) => {
			toast({
				message: `Failed to update preorder status: ${error.message}`,
				variant: 'error',
			});
		},
	});
};

export const useBulkUpdatePreorderStatus = (): UseMutationResult<void, Error, BulkUpdatePreorderStatusParams> => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ preorderIds, status }: BulkUpdatePreorderStatusParams): Promise<void> => {
			if (!servicesProviderId) {
				throw new Error('Cannot update preorder statuses without services provider ID');
			}
			const results = await Promise.allSettled(
				preorderIds.map((id) => updatePreorderStatus(servicesProviderId, id, status)),
			);
			const failures = results.filter((r) => r.status === 'rejected');
			if (failures.length > 0) {
				throw new Error(`${failures.length} of ${preorderIds.length} status updates failed`);
			}
		},
		onSuccess: (_data, { preorderIds, status }) => {
			const displayStatus = PREORDER_STATUS_DISPLAY_LABEL[status] ?? status;
			for (const preorderId of preorderIds) {
				updatePreorderStatusInCache({
					queryClient,
					preorderId,
					newDisplayStatus: displayStatus,
				});
			}
			toast({
				message: `${preorderIds.length} preorder${preorderIds.length === 1 ? '' : 's'} updated`,
				variant: 'success',
			});
			// Delay invalidation — Algolia indexes asynchronously, so an immediate refetch
			// would return stale data and overwrite our cache update
			setTimeout(() => queryClient.invalidateQueries({ queryKey: preorders._def }), 3000);
		},
		onError: (error) => {
			toast({ message: error.message, variant: 'error' });
		},
	});
};
